import { Injectable } from '@angular/core';
import { AppConfigService } from './app-config.service';
import { AppService } from './app.service';
import { UserConfigService } from './user-config.service';
import { requestResponse } from '../models/service-response.model';
import type { SessionUserDto, SessionValidateDto } from '../dtos/session/session-user.dto';

/**
 * API de sesión en bela-belleza-api (header `Secret` con JWT del login).
 */
@Injectable({ providedIn: 'root' })
export class SessionApiService {
  private menuBootstrapDone = false;

  constructor(
    private readonly appService: AppService,
    private readonly appConfigService: AppConfigService,
  ) {}

  private get baseUrl(): string {
    const base = this.appConfigService.config.servicesAPI || '';
    return base.endsWith('/') ? base : `${base}/`;
  }

  private get validateUrl(): string {
    return `${this.baseUrl}session/validate`;
  }

  private get meUrl(): string {
    return `${this.baseUrl}user/me`;
  }

  /** Invalida menú en memoria (p. ej. tras logout). */
  clearBootstrap(): void {
    this.menuBootstrapDone = false;
  }

  /**
   * GET session/validate — acceso al sistema.
   */
  async getValidate(): Promise<SessionValidateDto | null> {
    this.appConfigService.setApisURL();
    if (!this.appConfigService.config.servicesAPI?.trim()) {
      return null;
    }
    try {
      const response = (await this.appService.get(this.validateUrl)) as requestResponse;
      return (response?.data ?? null) as SessionValidateDto | null;
    } catch {
      return null;
    }
  }

  /**
   * GET user/me — datos de usuario y pantallas.
   */
  async getMe(): Promise<SessionUserDto | null> {
    this.appConfigService.setApisURL();
    if (!this.appConfigService.config.servicesAPI?.trim()) {
      return null;
    }
    try {
      const response = (await this.appService.get(this.meUrl)) as requestResponse;
      return (response?.data ?? null) as SessionUserDto | null;
    } catch {
      return null;
    }
  }

  /**
   * Valida sesión y, si aplica, carga menú/usuario desde `user/me` una sola vez.
   */
  async ensureAppSession(userConfig: UserConfigService): Promise<SessionValidateDto | null> {
    const v = await this.getValidate();
    if (!v) {
      return null;
    }
    if (!v.allowed) {
      return v;
    }
    if (!this.menuBootstrapDone) {
      const me = await this.getMe();
      if (!me) {
        return null;
      }
      userConfig.applySessionBootstrap(me);
      this.menuBootstrapDone = true;
    }
    return v;
  }
}
